import { validators } from '../utils';
import { makeFormComponent, makeFormHook } from '../utils/factory';
import type { InferFormType } from '../utils/types';

/**
 * Legacy account creation form schema for the old signup flow (is_signup_changes_enabled: false).
 * Requires birthday and phone number during signup, with 12-character password minimum.
 * Used by the restored AcceptInvite components.
 */
export const accountCreationSchemaLegacy = validators
  .object({
    birthday: validators.birthdateAdult(),
    confirmPassword: validators
      .string({ message: 'Password is required' })
      .min(12, { message: 'Password must be at least 12 characters' }),
    email: validators.email(),
    firstName: validators.requiredString('First name'),
    lastName: validators.requiredString('Last name'),
    nickname: validators.optionalString(),
    password: validators
      .string({ message: 'Password is required' })
      .min(12, { message: 'Password must be at least 12 characters' }),
    phoneNumber: validators.e164PhoneNumber(),
    promoCode: validators.optionalString(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type AccountCreationSchemaLegacy = InferFormType<typeof accountCreationSchemaLegacy>;

export const accountCreationSchemaLegacyDefaultValues = {
  birthday: '' as unknown as ReturnType<typeof validators.birthdateAdult>['_output'],
  confirmPassword: '',
  email: '',
  firstName: '',
  lastName: '',
  nickname: '',
  password: '',
  phoneNumber: '',
  promoCode: '',
} satisfies AccountCreationSchemaLegacy;

export const useAccountCreationFormLegacy = makeFormHook(
  accountCreationSchemaLegacy,
  accountCreationSchemaLegacyDefaultValues,
);

export const AccountCreationFormLegacy = makeFormComponent(
  accountCreationSchemaLegacy,
  accountCreationSchemaLegacyDefaultValues,
);

/**
 * Account creation form schema for sign-up and accept-invite flows.
 * Phone number and birthday are optional here - they are collected separately
 * in the post-signup onboarding flow.
 */
export const accountCreationFormSchema = validators
  .object({
    confirmPassword: validators.password(),
    email: validators.email(),
    firstName: validators.requiredString('First name'),
    lastName: validators.requiredString('Last name'),
    nickname: validators.optionalString(),
    password: validators.password(),
    // phoneNumber is temporarily optional as a workaround for invites.
    phoneNumber: validators.e164PhoneNumber().optional(),
    promoCode: validators.optionalString(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type AccountCreationFormSchema = InferFormType<typeof accountCreationFormSchema>;

export const accountCreationFormDefaultValues = {
  confirmPassword: '',
  email: '',
  firstName: '',
  lastName: '',
  nickname: '',
  password: '',
  promoCode: '',
} satisfies AccountCreationFormSchema;

export const useAccountCreationForm = makeFormHook(accountCreationFormSchema, accountCreationFormDefaultValues);

export const AccountCreationForm = makeFormComponent(accountCreationFormSchema, accountCreationFormDefaultValues);
